import { useLayoutViewContext } from '@src/features/LayoutViewExpose/LayoutViewContext';

import axiosClient from '@app/configs/axios';

import useLangText from '@shared/hooks/useLangText';
import { useQueryFetch } from '@shared/hooks/useQueryFetch';
import { useQueryMutation } from '@shared/hooks/useQueryMutation';
import ProSpinner from '@shared/newComponents/ProSpinner';
import {
    ProcessFieldBase,
    type ProcessFieldFile,
    ServiceVM,
} from '@shared/types/process';
import { DummyFile } from '@shared/types/types';
import { base64ToBlob } from '@shared/util/base64ToBlob';
import { getFileMetaText } from '@shared/util/fileMeta';
import { getExtensionFiles as getAllExtensionFiles } from '@shared/util/getExtensionFiles';
import getUrl from '@shared/util/getUrl';
import { isDeepEqual } from '@shared/util/isDeepEqual';

import docxIcon from '@assets/icons/docx.svg';
import gifIcon from '@assets/icons/gif.svg';
import jpgIcon from '@assets/icons/jpg.svg';
import mp3Icon from '@assets/icons/mp3.svg';
import mp4Icon from '@assets/icons/mp4.svg';
import pdfIcon from '@assets/icons/pdf.svg';
import pngIcon from '@assets/icons/png.svg';
import xlsxIcon from '@assets/icons/xlsx.svg';

import { FileContent, FilePreview } from '../../FilePreview';
import { FileResponse } from '../../ShadcnUploadFlowClient';
import ShadcnUploadFlowClient from '../../ShadcnUploadFlowClient';

import { ShadcnButton, ShadcnForm } from '@shadcn-features/core';
import { Download, Eye, FileText, Upload } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props {
    field: ProcessFieldBase & ProcessFieldFile;
    name: string;
    service?: ServiceVM;
    onChange: (value: string[]) => void;
    value: string[];
    label?: React.ReactNode;
    readOnly?: boolean;
    required?: boolean;
    fileUploadController?: string;
    variant?: 'default' | 'ai-chat';
    disabled?: boolean;
}

export type ReadonlyFile = DummyFile & {
    created?: string | Date | null;
    createdAt?: string | Date | null;
    date?: string | Date | null;
    lastModified?: string | Date | null;
    size?: number | null;
};

function InnerProcessFieldFileField({
    field,
    value,
    onChange,
    service,
    fileUploadController,
    variant = 'default',
    ...props
}: Props) {
    const form = useFormContext();
    const context: ReturnType<typeof useLayoutViewContext> | null =
        useLayoutViewContext({
            ignoreError: true,
        });

    const controller =
        context?.fileUploadController ??
        fileUploadController ??
        'flow/Services';

    const serviceId = service?.id ?? '';

    const { accept, extension } = useMemo<{
        accept: string | null | undefined;
        extension: string[] | null | undefined;
    }>(() => {
        if (
            !field.options.acceptTypes ||
            field.options.acceptTypes.length === 0
        ) {
            return { accept: null, extension: null };
        }

        return getAllExtensionFiles(field.options.acceptTypes);
    }, [field.options.acceptTypes]);

    const propsItem = {
        ...field.options,
        value: [],
        ...props,
    };

    const { data: dataFiles, isFetching: isFilesFetching } = useQueryFetch<
        DummyFile[]
    >(getUrl(controller, 'Files', serviceId, field.id), {
        onSuccess(params) {
            const nextValue = params.data?.map((item) => item.id) ?? [];
            const currentValue = form.getValues(props.name) as
                | string[]
                | null
                | undefined;

            if (!isDeepEqual(currentValue, nextValue)) {
                form.setValue(props.name, nextValue, {
                    shouldDirty: false,
                    shouldTouch: false,
                    shouldValidate: false,
                });
            }

            if (nextValue.length > 0) {
                form.clearErrors(props.name);
            } else if (currentValue != null) {
                form.setValue(props.name, null, {
                    shouldDirty: false,
                    shouldTouch: false,
                    shouldValidate: false,
                });
            }
        },

        hideErrorNotification: true,
        enabled: !!field.id,
    });

    const { mutateAsync: onUpload, isPending: isUploadPending } =
        useQueryMutation<FormData, string>(
            getUrl(controller, 'UploadFile'),
            'post',
            {
                invalidateOnSuccess: [
                    [getUrl(controller, 'Files', serviceId, field.id)],
                    [`${controller}/GetGeneratedDocuments/${serviceId}`],
                ],
                params: {
                    ServiceProcessFieldId: field.id,
                    ServiceId: serviceId,
                },
                onSuccess(params) {
                    const data = value && Array.isArray(value) ? value : [];
                    onChange([...data, params.data]);
                },
            },
        );

    const { mutateAsync: onDownload } = useQueryMutation<
        {
            id: string;
        },
        { content: string; extension: string; name: string; id: string }
    >(getUrl('flow/Files', 'GetBase64'), 'get', {
        variablesOption(url, variables, axiosConfig) {
            return {
                url: url + '/' + variables.id,
                ...axiosConfig,
            };
        },
    });

    const { mutateAsync: onDelete, isPending: isDeletePending } =
        useQueryMutation<{
            id: string;
        }>(getUrl(controller, 'DeleteFile', serviceId, field.id), 'delete', {
            invalidateOnSuccess: [
                [getUrl(controller, 'Files', serviceId, field.id)],
                [`${controller}/GetGeneratedDocuments/${serviceId}`],
            ],
            variablesOption(url, variables, axiosConfig) {
                return {
                    url: url + '/' + variables.id,
                    ...axiosConfig,
                };
            },
            onSuccess(params) {
                onChange(
                    value?.filter?.((f) => f !== params?.variables?.id) ?? [],
                );
            },
        });

    const allFiles = useMemo(() => {
        if (field.defaultValue && Array.isArray(field.defaultValue)) {
            const newData = (dataFiles ?? []).map((f) => ({
                ...f,
                isDelete: !field.defaultValue?.includes(f.id),
            }));
            return newData;
        }
        return dataFiles;
    }, [dataFiles, field.defaultValue]);

    if (props.readOnly && variant !== 'ai-chat') {
        return (
            <ReadonlyFileList files={allFiles ?? []} onDownload={onDownload} />
        );
    }

    return (
        <ProSpinner
            spinning={isUploadPending || isDeletePending || isFilesFetching}
        >
            <div className='flex flex-col gap-1'>
                <ShadcnUploadFlowClient
                    {...propsItem}
                    variant={variant}
                    enablePdfAiAnalysis
                    accept={accept ?? undefined}
                    extensions={extension ?? undefined}
                    value={allFiles ?? []}
                    previewDrawerProps={{
                        width: '1000px',
                        styles: {
                            body: {
                                overflowY: 'hidden',
                            },
                        },
                    }}
                    onDelete={(file) => void onDelete({ id: file.id })}
                    onDownload={onDownload}
                    onUpload={(file) => {
                        if (!serviceId || !props.name) return;
                        const formData = new FormData();
                        formData.set('File', file);
                        formData.set('ServiceProcessFieldId', props.name);
                        formData.set('ServiceFieldId', field.id ?? '');
                        return onUpload(formData).then(() => undefined);
                    }}
                    maxSize={
                        variant === 'ai-chat'
                            ? field.options.maxSize || 1024
                            : 1024
                    }
                />
            </div>
        </ProSpinner>
    );
}

export function ReadonlyFileList({
    files,
    onDownload,
}: {
    files: ReadonlyFile[];
    onDownload: (params: {
        id: string;
    }) => Promise<FileContent | undefined> | FileContent | undefined;
}) {
    const { t } = useTranslation('translation');

    if (!files.length) {
        return (
            <div className='flex min-h-[65px] items-center rounded-xl bg-(--color-body) p-[15px] text-sm text-(--color-card-subtitle)'>
                {t('translation:noFile')}
            </div>
        );
    }

    return (
        <div className='flex w-full flex-col gap-2.5'>
            {files.map((file) => (
                <ReadonlyFileCard
                    key={file.id}
                    file={file}
                    onDownload={onDownload}
                />
            ))}
        </div>
    );
}

function ReadonlyFileCard({
    file,
    onDownload,
}: {
    file: ReadonlyFile;
    onDownload: (params: {
        id: string;
    }) => Promise<FileContent | undefined> | FileContent | undefined;
}) {
    const { t } = useTranslation('translation');
    const extension = getFileExtension(file);
    const metaText = getFileMetaText(file);

    return (
        <div className='grid min-h-[65px] w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-[15px] rounded-xl bg-(--color-body) p-[15px]'>
            <FileTypeIcon extension={extension} />

            <div className='flex min-w-0 flex-col gap-0.5 break-words'>
                <div
                    className='w-full truncate text-sm leading-normal font-medium text-[var(--color-card-text)]'
                    title={file.name}
                >
                    {file.name}
                </div>
                {metaText && (
                    <div
                        className='w-full truncate text-[11px] leading-normal font-normal text-(--color-card-subtitle)'
                        title={metaText}
                    >
                        {metaText}
                    </div>
                )}
            </div>

            <div className='flex items-center justify-end gap-1'>
                <FilePreview
                    enablePdfAiAnalysis
                    file={file}
                    previewText={t('translation:preview')}
                    wrapperType='modal'
                    onDownload={onDownload}
                    renderTriggerBtn={(cb) => (
                        <button
                            className='flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-(--color-border) bg-transparent p-0 text-(--color-card-text) shadow-xs hover:text-(--color-brand-green) focus-visible:outline-2 focus-visible:outline-(--color-brand-green)'
                            aria-label={t('translation:preview')}
                            type='button'
                            onClick={async () => {
                                cb();
                            }}
                        >
                            <Eye size={14} />
                        </button>
                    )}
                />
                <button
                    className='flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-(--color-border) bg-transparent p-0 text-(--color-card-text) shadow-xs hover:text-(--color-brand-green) focus-visible:outline-2 focus-visible:outline-(--color-brand-green)'
                    aria-label={t('translation:download')}
                    type='button'
                    onClick={async () => {
                        const data = await onDownload(file);

                        if (!data?.content) return;

                        const fileResponse = data as FileResponse;
                        const contentType =
                            'contentType' in fileResponse
                                ? fileResponse.contentType
                                : 'extension' in fileResponse
                                  ? fileResponse.extension
                                  : data.extension;
                        const fileName =
                            'originalFileName' in fileResponse
                                ? fileResponse.originalFileName
                                : 'name' in fileResponse
                                  ? fileResponse.name
                                  : data.name;

                        const blob = base64ToBlob(data.content, contentType);
                        const url = URL.createObjectURL(blob);
                        const downloadLink = document.createElement('a');
                        downloadLink.href = url;
                        downloadLink.download = fileName || file.name;
                        downloadLink.click();
                        downloadLink.remove();
                        URL.revokeObjectURL(url);
                    }}
                >
                    <Download size={14} />
                </button>
            </div>
        </div>
    );
}

function FileTypeIcon({ extension }: { extension: string }) {
    const icon = getFileIcon(extension);

    if (icon) {
        return (
            <img
                alt={extension}
                className='size-[26px] shrink-0 object-contain'
                src={icon}
            />
        );
    }

    return (
        <FileText className='shrink-0 text-[var(--color-danger)]' size={28} />
    );
}

function getFileIcon(extension: string) {
    switch (extension) {
        case 'jpg':
        case 'jpeg':
            return jpgIcon;
        case 'mp4':
            return mp4Icon;
        case 'pdf':
            return pdfIcon;
        case 'png':
            return pngIcon;
        case 'xlsx':
            return xlsxIcon;
        case 'docx':
            return docxIcon;
        case 'mp3':
            return mp3Icon;
        case 'gif':
            return gifIcon;
        default:
            return null;
    }
}

function getFileExtension(file: ReadonlyFile) {
    const fromName = file.name.split('.').pop();
    if (fromName) return fromName.toLowerCase();

    const fromType = file.fileType.split('/').pop();
    return fromType?.toLowerCase() ?? '';
}

export function ProcessFieldFileField(props: Props) {
    const { t } = useTranslation('translation');
    const getLangText = useLangText();

    const { data: templateField } = useQueryFetch<DummyFile[]>(
        `flow/ServiceProcessFields/Files/${props.field.id}`,
        {
            enabled:
                !!props.field.id || props.field.options.isDefaultFile === true,
        },
    );

    const handleDownload = useCallback(async () => {
        if (!templateField) return;

        for (const file of templateField) {
            const res = await axiosClient.get(
                `flow/Files/GetBase64/${file.id}`,
            );

            if (res.data.data.content && res.data.data.extension) {
                downloadFile(
                    res.data.data.content,
                    file.name,
                    res.data.data.extension,
                );
            }
        }
    }, [templateField]);

    const templateDownload = templateField && templateField.length > 0 && (
        <div className='flex items-center gap-1 text-[#1890FF]'>
            <Upload size={16} />
            <ShadcnButton
                className='border-b-[#1890FF] text-inherit'
                variant='borderBottomRedText'
                popconfirm={{
                    title: t('translation:areYouSure'),
                    onConfirm: handleDownload,
                }}
            >
                {t('translation:downloadTemplate')}
            </ShadcnButton>
        </div>
    );

    return (
        <>
            <ShadcnForm.Item
                {...props}
                label={props.variant === 'ai-chat' ? undefined : props.label}
                className={
                    props.variant === 'ai-chat' ? 'mb-0! w-full' : undefined
                }
                tooltip={getLangText(props.field?.tooltip ?? '')}
                extraLabel={
                    templateDownload && (
                        <div className='ml-4'>{templateDownload}</div>
                    )
                }
            >
                {({ value, onChange }) => (
                    <>
                        {props.variant === 'ai-chat' && props.label && (
                            <div
                                className='flex flex-wrap items-center justify-between gap-2 border-b border-solid border-(--color-card-text)/10 px-[18px] py-4 text-sm leading-5 font-medium text-(--color-card-text)'
                                title={getLangText(props.field.tooltip ?? '')}
                            >
                                {props.label}
                                {templateDownload}
                            </div>
                        )}
                        <div
                            className={
                                props.variant === 'ai-chat'
                                    ? 'px-[18px] py-3'
                                    : undefined
                            }
                        >
                            <InnerProcessFieldFileField
                                {...props}
                                value={value as string[]}
                                onChange={onChange}
                            />
                        </div>
                    </>
                )}
            </ShadcnForm.Item>
        </>
    );
}

function downloadFile(base64: string, fileName: string, fileType: string) {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: fileType });

    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();

    URL.revokeObjectURL(url);
}
